import React, { useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import SPAInput, { spaInputClass } from '@/components/admin/SysadminSPA/shared/SPAInput';
import SPAPill from '@/components/admin/SysadminSPA/shared/SPAPill';
import cn from '@/utils/cn';
import DeleteNoteButton, { iconButtonClass } from './DeleteNoteButton';
import { focusRingClass } from './NoteForm';
import noteDate from './noteDate';
import StatusPill from './StatusPill';
import type { NoteValues, SlideStatus } from './useSlideStatuses';

// The draft being edited lives in a ref, not state: each editor keeps its own input state and writes
// through to the ref, so typing re-renders one field instead of the dialog and the whole table.
interface ColumnActions {
  draft: React.MutableRefObject<NoteValues>;
  editingId?: number;
  isDeleting: boolean;
  isMarking: boolean;
  isSaving: boolean;
  onCancelEdit: () => void;
  onDelete: (id: number) => Promise<unknown>;
  onEdit: (note: SlideStatus) => void;
  onSaveEdit: () => void;
  onSetDone: (note: SlideStatus, done: boolean) => void;
  stafferId: number;
  statuses: string[];
}

const columnHelper = createColumnHelper<SlideStatus>();

const DateCell = ({ iso }: { iso: string }) => {
  const { date, time } = noteDate(iso);

  return (
    <span className="tw-whitespace-nowrap tw-tabular-nums tw-text-spa-secondary">
      {date}
      <span className="tw-block tw-text-xs tw-text-spa-tertiary">{time}</span>
    </span>
  );
};

// The done mark keeps the note text intact: the text dims and a badge below it says when and by whom.
const NoteCell = ({ note }: { note: SlideStatus }) => {
  if (!note.completed_at) return <span className="tw-text-spa-primary">{note.note}</span>;

  const { date, time } = noteDate(note.completed_at);

  return (
    <span className="tw-flex tw-flex-col tw-gap-1">
      <span className="tw-text-spa-tertiary">{note.note}</span>
      <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-spa-tertiary">
        <SPAPill tone="success">Done</SPAPill>
        <span title={`${date} at ${time}`}>{date}</span>
        {note.completed_by_name && ` · ${note.completed_by_name}`}
      </span>
    </span>
  );
};

const DoneToggle = ({ actions, note }: { actions: ColumnActions; note: SlideStatus }) => {
  const done = Boolean(note.completed_at);

  return (
    <button
      aria-label={done ? 'Reopen note' : 'Mark note done'}
      aria-pressed={done}
      className={cn(iconButtonClass, done && 'tw-text-spa-success hover:tw-text-spa-success')}
      disabled={actions.isMarking}
      title={done ? 'Reopen' : 'Mark done'}
      type="button"
      onClick={() => actions.onSetDone(note, !done)}
    >
      <i aria-hidden="true" className={done ? 'fa fa-check-circle' : 'fa fa-check-circle-o'} />
    </button>
  );
};

const AuthorCell = ({ isYou, name }: { isYou: boolean; name: string }) => (
  <span className="tw-whitespace-nowrap tw-text-spa-secondary">
    {name}
    {isYou && <span className="tw-ml-1 tw-text-xs tw-text-spa-tertiary">(you)</span>}
  </span>
);

// A native select rather than SPASelect: its menu can't be clipped by the table's scroll wrapper,
// and it needs no portal, which the modal's pointer-events lock would swallow anyway.
const StatusEditor = ({ actions }: { actions: ColumnActions }) => {
  const [status, setStatus] = useState(actions.draft.current.status);
  const [options] = useState(
    actions.statuses.includes(status) ? actions.statuses : [status, ...actions.statuses],
  );

  const change = (value: string) => {
    actions.draft.current.status = value;
    setStatus(value);
  };

  return (
    <select
      aria-label="Note assignee"
      className={cn(spaInputClass, focusRingClass, 'tw-w-auto tw-min-w-[7rem]')}
      value={status}
      onChange={event => change(event.target.value)}
    >
      {options.map(option => <option key={option} value={option}>{option}</option>)}
    </select>
  );
};

const NoteEditor = ({ actions }: { actions: ColumnActions }) => {
  const [note, setNote] = useState(actions.draft.current.note);

  const change = (value: string) => {
    actions.draft.current.note = value;
    setNote(value);
  };

  return (
    <SPAInput
      autoFocus
      aria-label="Note text"
      className={focusRingClass}
      value={note}
      onChange={event => change(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') actions.onSaveEdit();
      }}
    />
  );
};

const EditActions = ({ actions }: { actions: ColumnActions }) => (
  <span className="tw-flex tw-justify-end tw-gap-1">
    <button
      aria-label="Save note"
      className={cn(iconButtonClass, 'tw-text-spa-success hover:tw-text-spa-success')}
      disabled={actions.isSaving}
      type="button"
      onClick={actions.onSaveEdit}
    >
      <i aria-hidden="true" className="fa fa-check" />
    </button>
    <button
      aria-label="Cancel edit"
      className={cn(iconButtonClass, 'tw-text-spa-error hover:tw-text-spa-error')}
      type="button"
      onClick={actions.onCancelEdit}
    >
      <i aria-hidden="true" className="fa fa-times" />
    </button>
  </span>
);

const RowActions = ({ actions, note }: { actions: ColumnActions; note: SlideStatus }) => {
  if (actions.editingId === note.id) return <EditActions actions={actions} />;

  return (
    <span className="tw-flex tw-justify-end tw-gap-1">
      <DoneToggle actions={actions} note={note} />
      <button aria-label="Edit note" className={iconButtonClass} type="button" onClick={() => actions.onEdit(note)}>
        <i aria-hidden="true" className="fa fa-edit" />
      </button>
      <DeleteNoteButton isDeleting={actions.isDeleting} onConfirm={() => actions.onDelete(note.id)} />
    </span>
  );
};

const isEditing = (actions: ColumnActions, note: SlideStatus) => actions.editingId === note.id;

const noteColumns = (actions: ColumnActions): ColumnDef<SlideStatus, unknown>[] => [
  columnHelper.accessor('created_at', {
    cell: info => <DateCell iso={info.getValue()} />,
    header: 'Date',
    size: 120,
  }),
  columnHelper.accessor('status', {
    cell: info => (isEditing(actions, info.row.original)
      ? <StatusEditor key={info.row.original.id} actions={actions} />
      : <StatusPill status={info.getValue()} />),
    header: 'Assignee',
    size: 110,
  }),
  columnHelper.accessor('note', {
    cell: info => (isEditing(actions, info.row.original)
      ? <NoteEditor key={info.row.original.id} actions={actions} />
      : <NoteCell note={info.row.original} />),
    enableSorting: false,
    header: 'Note',
    size: 320,
  }),
  columnHelper.accessor('staffer_name', {
    cell: info => <AuthorCell isYou={info.row.original.staffer_id === actions.stafferId} name={info.getValue()} />,
    header: 'By',
    size: 140,
  }),
  columnHelper.display({
    cell: info => <RowActions actions={actions} note={info.row.original} />,
    enableSorting: false,
    header: '',
    id: 'actions',
    size: 120,
  }),
];

export default noteColumns;
